import { removeStoredMutationsById } from '../graphql/util/removeStoredMutationsById'
import type { CacheProxy, Deal } from '@paintscout/api'
import {
  updateCachedDeal,
  useSaveDealMutation,
  useDeleteDealsMutation,
  useArchiveDealsMutation,
  useUntrashDealsMutation,
  SearchDealsDocument,
  updateCachedDealPartially
} from '@paintscout/api'
import type ApolloCache from '@wora/apollo-cache'
import { useMemo } from 'react'
import { useApolloClient } from '@apollo/react-hooks'
import { getOperationName } from 'apollo-link'

/**
 * Returns common useMutations for saving deals and handles updating the caches
 */
export function useDealMutations() {
  const [saveDealMutation] = useSaveDealMutation()
  const [deleteDealsMutation] = useDeleteDealsMutation()
  const [archiveDealsMutation] = useArchiveDealsMutation()
  const [untrashDealsMutation] = useUntrashDealsMutation()

  const client = useApolloClient()

  return useMemo(
    () => ({
      async saveDeal({ deal }: { deal: Deal }) {
        const timeOfSave = Date.now()
        const mutationId = `save-deal-${deal._id}`
        const result = await saveDealMutation({
          variables: {
            deal
          },
          context: {
            __mutation_id__: mutationId
          },

          optimisticResponse: {
            saveDeal: {
              __typename: 'Deal' as const,
              ...deal,
              owner: deal.owner
                ? {
                    __typename: 'Owner',
                    ...deal.owner
                  }
                : null,
              stage: deal.stage
                ? {
                    __typename: 'DealStage',
                    ...deal.stage
                  }
                : null,
              pipeline: deal.pipeline
                ? {
                    __typename: 'DealPipeline',
                    ...deal.pipeline
                  }
                : null,
              contact: deal.contact
                ? {
                    __typename: 'DealContact',
                    ...deal.contact
                  }
                : null,
              _rev: deal._rev // null is important, if its undefined it will break cache for deal (undefined is not valid graphql value)
            }
          },
          update(cache: CacheProxy, { data }) {
            if (data?.saveDeal) {
              updateCachedDeal(cache, data.saveDeal)
            }
          },
          refetchQueries: ['searchDeals', 'getDeal', 'searchDealsByCompany']
        })

        removeStoredMutationsById(client, mutationId, { before: timeOfSave })

        return {
          deal: result.data?.saveDeal
        }
      },

      async deleteDeals(ids: string[], skipRefetchQueries = false) {
        return deleteDealsMutation({
          variables: { ids },
          optimisticResponse: {
            deleteDeals: ids.map((id) => ({
              __typename: 'BulkDocsResponseObject',
              _id: id,
              _rev: null,
              error: null
            }))
          },
          refetchQueries: skipRefetchQueries ? [] : ['searchDeals'],
          update(cache: ApolloCache, { data: _data }) {
            ids.forEach((id) => {
              // Mark deal as trashed so it's filtered out from searches
              updateCachedDealPartially(cache, id, {
                trashed: true,
                _deleted: true
              })
            })
          }
        })
      },
      async untrashDeals(ids: string[]) {
        return untrashDealsMutation({
          variables: { ids },
          refetchQueries: ['searchDeals', 'getDeal', 'searchDealsByCompany', 'searchQuotes'],
          update(cache: ApolloCache, { data: _data }) {
            ids.forEach((id) => {
              updateCachedDealPartially(cache, id, { trashed: false })
            })
          }
        })
      },

      async archiveDeals(
        dealIds: string[],
        { unarchive, skipRefetchQueries = false }: { unarchive?: boolean; skipRefetchQueries?: boolean } = {}
      ) {
        return archiveDealsMutation({
          variables: { ids: dealIds, unarchive },
          refetchQueries: skipRefetchQueries ? [] : [getOperationName(SearchDealsDocument)],
          optimisticResponse: {
            archiveDeals: dealIds.map((id) => ({
              __typename: 'BulkDocsResponseObject',
              _id: id,
              _rev: null,
              error: null,
              reason: null
            }))
          },
          update(cache: ApolloCache, { data: _data }) {
            dealIds.forEach((id) => {
              // we can fake the deal document as just an object with archived flag
              updateCachedDealPartially(cache, id, { archived: !unarchive })
            })
          }
        })
      }
    }),
    []
  )
}
